import { Controller } from 'stimulus'
import { get } from 'lodash'

export default class extends Controller {
  static targets = ['content', 'close']
  static values = {
    type: String,
    backgroundTarget: String // which background to make inert (CSS selector). Optional.
  }

  connect() {
    this._onOverlayOpening = this.onOverlayOpening.bind(this)
    this._onOverlaySuccess = this.close.bind(this)
    this._onBeforeStreamRender = this.beforeStreamRender.bind(this)

    document.addEventListener('overlay:opening', this._onOverlayOpening)
    document.addEventListener('overlay:success', this._onOverlaySuccess)
    document.addEventListener('turbo:before-stream-render', this._onBeforeStreamRender)
  }

  disconnect() {
    document.removeEventListener('overlay:opening', this._onOverlayOpening)
    document.removeEventListener('overlay:success', this._onOverlaySuccess)
    document.removeEventListener('turbo:before-stream-render', this._onBeforeStreamRender)
  }

  // Triggered before a turbo_stream is rendered; show when this overlay is the target
  beforeStreamRender(e) {
    if (e.target.target == this.typeValue) {
      this.startLoading(e)
      e.detail.render(e.detail.newStream)
      this.stopLoading(e)
    }
  }

  startLoading(e) {
    if (e?.target?.querySelector?.('[data-overlay=keep-open]')) return
    const requestedFrameId = this.determineRequestedFrameId(e)
    if (requestedFrameId && requestedFrameId !== this.typeValue) return

    // Tell others to close, then open this one
    document.dispatchEvent(new CustomEvent('overlay:opening', {
      bubbles: true,
      detail: { opener: this.element }
    }))

    this.element.classList.add('show', 'loading')
    this.updateBackgroundInert()
  }

  stopLoading(_e) {
    this.element.classList.remove('loading')
  }

  closeOnEsc(e) {
    if (e.code === 'Escape') this.close()
  }

  close() {
    if (!this.isOpen) return
    this.element.classList.remove('show')
    this.closeContent()
    this.updateBackgroundInert()
  }

  closeSubmit(e) {
    const formSubmission = e.detail.formSubmission
    const overlay = get(formSubmission, 'submitter.dataset.overlay', 'default')

    if (overlay === 'keep-open') return
    if (formSubmission.method === 'GET') return // GETs keep the overlay open

    if (e.target.closest('[data-controller=overlay]') && e.detail.success) {
      this.dispatch('success') // triggers overlay:success -> close()
    }
  }

  // --- Helpers --------------------------------------------------------------

  determineRequestedFrameId(e) {
    const headerObj = e?.detail?.fetchOptions?.headers
    const headerFrameId = headerObj?.['Turbo-Frame']
    const datasetFrameId = e?.target?.getAttribute?.('data-turbo-frame')
    const requestedFrameId = datasetFrameId || headerFrameId
    return requestedFrameId
  }

  onOverlayOpening(e) {
    const opener = e.detail?.opener
    if (opener && opener !== this.element) this.close()
  }

  get isOpen() {
    return this.element.classList.contains('show')
  }

  closeContent() {
    if (this.hasContentTarget) {
      this.contentTarget.removeAttribute('src')
      this.contentTarget.innerHTML = ''
    }
  }

  // Is there any open overlay that declares a background target?
  anyOpenWithBackgroundTarget() {
    // Look for any open overlay element that has the data attribute
    return document.querySelector('[data-controller~="overlay"].show[data-overlay-background-target-value]')
  }

  updateBackgroundInert() {
    // If *this* overlay doesn't declare a target, we still might need to
    // clear inert if none remain open with a target.
    const anyOverlayWantsInert = !!this.anyOpenWithBackgroundTarget()

    // If this controller has no declared background target, we don't apply inert ourselves.
    if (!this.hasBackgroundTargetValue) {
      // But if none want inert, ensure any previously inerted elements are cleared for *our* target (none).
      // Nothing to do here.
      return
    }

    const nodes = document.querySelectorAll(this.backgroundTargetValue)
    nodes.forEach(node => {
      node.inert = anyOverlayWantsInert
    })
  }
}
